import { useState, useEffect, useMemo, Fragment } from "react";
import SearchSelect from "./SearchSelect";
import { buildSeasonFilterOptions, seasonFilterPlaceholder, isSeasonOpen } from "./seasonFilter";
import { computeSeasonMonthlyComparison, SEASON_MONTH_ORDER, cyclePositionForDate, ytdThroughPosition } from "./seasonComparisonCalc";
import { exportSeasonComparisonXlsx } from "./seasonComparisonExport";

/* Season Comparison — Fase 3 (tabla base + panel de fechas + totales +
   filtros) + Fase 4 (export a Excel, ver seasonComparisonExport.js).
   El card de métricas comparativas (Fase 5) sigue fuera de alcance.

   Layout (left to right), matching the user's real reference sheet
   (comparativo temporadas.xlsx) plus the additions confirmed for this
   feature:
     [Month]  [Acumulado Histórico: Cases|Profit|Acumulado]  [Season A: Month|Cases|Profit|Acumulado|Profit/box|Profit/day|Profit/hour(|Δ Profit|Δ Acumulado if Open)]  [Season B: ...]  ...
   Rows are months 1-12 (fixed); each season group's own numbers come
   from computeSeasonMonthlyComparison (live for Open, captured for
   Closed — see seasonComparisonCalc.js).

   Δ Profit / Δ Acumulado (columns CO/CP in the reference sheet) only
   apply to the currently-Open season group, compared month-by-month
   against the most recently Closed season (n-1) — Closed groups never
   carry these 2 extra columns, so a season group is either 7 or 9
   columns wide depending on its status. */

var T = {
  en: {
    title: "Season Comparison", subtitle: "Monthly profit & cases across seasons",
    seasonsFilter: "Seasons", historicalAccumulated: "Historical Accumulated",
    colMonth: "Month", colCases: "Cases", colProfit: "Profit", colAcumulado: "Acumulated",
    colProfitBox: "Profit/box", colProfitDay: "Profit/day", colProfitHour: "Profit/hour",
    colDeltaProfit: "Δ Profit", colDeltaAcumulado: "Δ Acumulado", yoyChange: "Year-over-Year Change",
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    totalRow: "TOTAL",
    seasonStart: "Season Start", endOfSeason: "End of Season", today: "Today", daysElapsed: "Days elapsed",
    noSeasons: "No seasons yet. Create one in Seasons.",
    selectSeasons: "Select at least one season to compare.",
    exportExcel: "Export to Excel",
    selectAll: "Select all", clearAllBtn: "Clear all",
    cardSeasonProgress: "Season Progress", cardProfitYtd: "Profit YTD", cardBestMonth: "Best Historical Month", cardVsPriorYear: "vs Same Month Prior Year",
    vsHistAvg: "vs historical avg", casesYtd: "Cases YTD", noData: "No data",
  },
  es: {
    title: "Comparativo de Temporadas", subtitle: "Ganancia y cajas mensuales entre temporadas",
    seasonsFilter: "Temporadas", historicalAccumulated: "Acumulado Histórico",
    colMonth: "Mes", colCases: "Cajas", colProfit: "Ganancia", colAcumulado: "Acumulado",
    colProfitBox: "Gan./caja", colProfitDay: "Gan./día", colProfitHour: "Gan./hora",
    colDeltaProfit: "Δ Utilidad", colDeltaAcumulado: "Δ Acumulado", yoyChange: "Cambio Interanual",
    months: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
    totalRow: "TOTAL",
    seasonStart: "Inicio de Temporada", endOfSeason: "Fin de Temporada", today: "Hoy", daysElapsed: "Días transcurridos",
    noSeasons: "Aún no hay temporadas. Crea una en Temporadas.",
    selectSeasons: "Selecciona al menos una temporada para comparar.",
    exportExcel: "Exportar a Excel",
    selectAll: "Seleccionar todas", clearAllBtn: "Limpiar",
    cardSeasonProgress: "Avance de Temporada", cardProfitYtd: "Utilidad YTD", cardBestMonth: "Mejor Mes Histórico", cardVsPriorYear: "vs. Mismo Mes Año Anterior",
    vsHistAvg: "vs. promedio histórico", casesYtd: "Cajas YTD", noData: "Sin datos",
  },
};

function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }
function fmtMoney(n) { var v = parseFloat(n); if (isNaN(v)) return "—"; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtInt(n) { var v = parseInt(n, 10); if (isNaN(v)) return "—"; return v.toLocaleString("en-US"); }
function fmtDate(d) { if (!d) return "—"; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }); }
function seasonLabel(s) {
  if (!s) return "?";
  if (s.seasonName) return s.seasonName;
  if (!s.startDate) return "?";
  var sy = s.startDate.slice(0, 4);
  var ey = s.endEstimate ? s.endEstimate.slice(0, 4) : null;
  return ey && ey !== sy ? sy + "-" + ey.slice(2) : sy;
}

/* Default filter: every operationally-open season (usually just the
   one active season) plus the 3 most recently ended Closed seasons. */
function defaultComparisonSeasonIds(seasons) {
  var openIds = seasons.filter(function (s) { return isSeasonOpen(s.status); }).map(function (s) { return String(s.id); });
  var closedSorted = seasons
    .filter(function (s) { return s.status === "closed"; })
    .sort(function (a, b) { return (b.closeDate || b.endEstimate || b.startDate || "").localeCompare(a.closeDate || a.endEstimate || a.startDate || ""); })
    .slice(0, 3)
    .map(function (s) { return String(s.id); });
  return openIds.concat(closedSorted);
}

/* Horizontal sum of every Closed season's per-month cases/profit, plus
   a running Acumulado down the months of that combined total. Always
   computed over ALL closed seasons regardless of the visible filter —
   this is the one column group that ignores the season selector.

   Follows SEASON_MONTH_ORDER (Aug->Jul), same as every other column
   group — August is where the combined running total starts, not
   January. This block spans many seasons with different start years,
   so unlike a single season's own rows it deliberately carries no
   `year` — only the shared leftmost Month column's name applies here. */
function computeHistoricalAccumulated(seasons) {
  var closed = seasons.filter(function (s) { return s.status === "closed"; });
  var byMonth = {};
  SEASON_MONTH_ORDER.forEach(function (m) { byMonth[m] = { cases: 0, profit: 0, hasAny: false }; });
  closed.forEach(function (s) {
    var result = computeSeasonMonthlyComparison(s);
    result.rows.forEach(function (r) {
      if (r.cases != null) { byMonth[r.month].cases += r.cases; byMonth[r.month].hasAny = true; }
      if (r.profit != null) { byMonth[r.month].profit += r.profit; byMonth[r.month].hasAny = true; }
    });
  });
  var running = 0;
  var out = [];
  SEASON_MONTH_ORDER.forEach(function (mm) {
    var b = byMonth[mm];
    if (b.hasAny) running += b.profit;
    out.push({ month: mm, cases: b.hasAny ? b.cases : null, profit: b.hasAny ? b.profit : null, acumulado: b.hasAny ? running : null });
  });
  return out;
}

/* Footer totals for one season's 12 derived rows. Profit/box, /day
   and /hour "weighted averages" reduce to totalProfit ÷ totalWeight —
   mathematically identical to averaging each month's ratio weighted by
   its own cases/business-days, without the extra pass. */
function computeSeasonFooter(rows) {
  var totalCases = 0, totalProfit = 0, totalDays = 0;
  rows.forEach(function (r) {
    if (r.cases != null) totalCases += r.cases;
    if (r.profit != null) totalProfit += r.profit;
    if (r.businessDays != null) totalDays += r.businessDays;
  });
  var profitBox = totalCases > 0 ? totalProfit / totalCases : 0;
  var profitDay = totalDays > 0 ? totalProfit / totalDays : 0;
  return { cases: totalCases, profit: totalProfit, profitBox: profitBox, profitDay: profitDay, profitHour: profitDay / 8 };
}

/* Most recently Closed season (n-1) regardless of the visible filter —
   used ONLY by the Fase 5 "vs Same Month Prior Year" card, which is
   deliberately about the real active season vs. its real predecessor,
   independent of whatever happens to be checked in the table's season
   filter. Same "ignore the filter" precedent as computeHistoricalAccumulated.
   NOT used by the table's own Δ columns anymore — see deltaTargetSeasons
   below, which IS filter-dependent. */
function findMostRecentClosedSeason(seasons) {
  return seasons
    .filter(function (s) { return s.status === "closed"; })
    .sort(function (a, b) { return (b.closeDate || b.endEstimate || b.startDate || "").localeCompare(a.closeDate || a.endEstimate || a.startDate || ""); })[0] || null;
}

/* The table's Δ Profit / Δ Acumulado columns compare the 2 MOST RECENT
   seasons in the CURRENT VISUAL SELECTION, by startDate — not "the
   Open one vs the most recent Closed one" regardless of status. Only
   the more-recent of the two gets the Δ columns; there's nothing to
   compare against with fewer than 2 selected. `selected` must already
   be sorted oldest->newest (matches selectedSeasons' own sort), so the
   2 most recent are simply its last two entries. */
function deltaTargetSeasons(selected) {
  if (selected.length < 2) return { current: null, previous: null };
  return { current: selected[selected.length - 1], previous: selected[selected.length - 2] };
}

/* Per-month Δ Profit / Δ Acumulado for one season vs. a comparison
   season, aligned by month number (1-12) — NOT by elapsed time, so
   "month 3 of the current season" always compares against "month 3 of
   the comparison season" regardless of when either actually started.
   `activeRows` must be in month order 1-12 (guaranteed by
   computeDerivedMonthly). Returns "—"-worthy nulls when the comparison
   season has no data for that month. */
/* Returns a lookup keyed by MONTH NUMBER (not array position) — rows
   now come back from computeSeasonMonthlyComparison in SEASON_MONTH_
   ORDER (Aug->Jul), not calendar 1-12, so a positional array would
   misalign the moment the render loop looks up "month m" instead of
   "array index i". */
function computeDeltaColumns(activeRows, previousSeason) {
  var out = {};
  if (!previousSeason) {
    activeRows.forEach(function (r) { out[r.month] = { deltaProfit: null, deltaAcumulado: null }; });
    return out;
  }
  var prevRows = computeSeasonMonthlyComparison(previousSeason).rows;
  var prevByMonth = {};
  prevRows.forEach(function (r) { prevByMonth[r.month] = r; });
  activeRows.forEach(function (r) {
    var prev = prevByMonth[r.month];
    /* "—" only when BOTH sides have nothing to compare — an
       uncaptured/missing side otherwise counts as 0, not as "no
       comparison possible" (e.g. a new season's first months vs a
       prior season that hadn't started yet should show the full
       amount as growth, not a blank). */
    var curProfit = r.profit != null ? r.profit : null;
    var prevProfit = (prev && prev.profit != null) ? prev.profit : null;
    var deltaProfit = (curProfit == null && prevProfit == null) ? null : (curProfit || 0) - (prevProfit || 0);
    var curAcum = r.acumulado != null ? r.acumulado : null;
    var prevAcum = (prev && prev.acumulado != null) ? prev.acumulado : null;
    var deltaAcumulado = (curAcum == null && prevAcum == null) ? null : (curAcum || 0) - (prevAcum || 0);
    out[r.month] = { deltaProfit: deltaProfit, deltaAcumulado: deltaAcumulado };
  });
  return out;
}

/* Negative -> red (below last season), positive -> green (above),
   zero/blank -> neutral gray. Used by the Fase 5 metrics cards
   (ArrowPct, line ~208) which don't render arrow text directly. */
function deltaColor(v) {
  if (v == null || v === 0) return "var(--color-text-muted)";
  return v > 0 ? "#059669" : "#DC2626";
}
function fmtDelta(v) {
  if (v == null) return "—";
  if (v === 0) return fmtMoney(0);
  return (v > 0 ? "▲ " : "▼ ") + fmtMoney(Math.abs(v));
}
/* Δ Profit / Δ Acumulado table cells color off the rendered arrow
   text itself, not a re-derived numeric sign — one source of truth
   for "what the user sees" instead of two things that could disagree.
   Color only, no added weight/size — zone C must render at exactly
   zone A's type scale (a plain `td`), the color is the only thing
   that's allowed to differ from a plain historical/season number. */
function deltaStyleFromText(text) {
  if (text.includes("▲")) return { color: "#00c853" };
  if (text.includes("▼")) return { color: "#ff3333" };
  return { color: "var(--color-text-muted)" };
}

/* Shared ▲/▼ + colored-% indicator for the Fase 5 metrics cards —
   same green-up/red-down/gray-neutral convention as deltaColor. */
/* Compact mode — cards were pushing the 12-month table below the fold
   on a standard ~1080px-tall monitor. Shrunk padding/font sizes so the
   whole cards section stays under ~70px tall regardless of wrap. */
function ArrowPct({ pct }) {
  if (pct == null) return <span style={{ color: "var(--color-text-muted)", fontWeight: 700 }}>—</span>;
  var up = pct >= 0;
  var color = pct === 0 ? "var(--color-text-muted)" : (up ? "#059669" : "#DC2626");
  return <span style={{ color: color, fontWeight: 700 }}>{pct !== 0 ? (up ? "▲" : "▼") : ""} {Math.abs(pct).toFixed(1)}%</span>;
}

var metricCardStyle = { flex: "1 1 200px", background: "var(--color-bg)", borderRadius: 8, padding: "6px 10px", display: "flex", flexDirection: "column", gap: 1, maxHeight: 66, overflow: "hidden" };
var metricLabelStyle = { fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-muted)", whiteSpace: "nowrap" };
var metricValueStyle = { fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", fontWeight: 800, fontSize: 18, color: "var(--color-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
var metricSubStyle = { fontSize: 10, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

var th = { padding: "6px 8px", textAlign: "right", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)", whiteSpace: "nowrap" };
var thGroup = { padding: "8px", textAlign: "center", fontWeight: 700, fontSize: 11, color: "var(--color-text)", borderBottom: "1px solid var(--color-border)", whiteSpace: "nowrap" };
var td = { padding: "5px 8px", textAlign: "right", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
var tdMonth = { padding: "5px 8px", textAlign: "left", fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

/* Layout: 3 fixed zones + 1 scrolling zone, all inside ONE <table> in
   ONE overflow-x:auto div (same freeze-pane technique the Δ columns
   already used) — sticky cells just need an explicit opaque
   background so the scrolling content underneath doesn't show through.
   Header z-index is pushed well above the global `.main-body table
   thead th { z-index: 10 }` rule (App.css) — zone B's own season
   headers get that default untouched, so zone A/C need to clear it or
   they'd render BELOW zone B's headers while scrolling.

   Zone A (sticky-left): Month + the 3 Historical Accumulated columns.
   Zone B (scrolls): each selected season's own 7 columns (Month,
   Cases, Profit, Acumulado, Profit/box, Profit/day, Profit/hour) —
   white background so it visually reads as the "moving" zone.
   Zone C (sticky-right), left to right: Δ Profit + Δ Acumulado (the
   boundary against zone B) then a duplicated Month column (so the Δ
   reader still has a month label at a glance, without it reading like
   part of the delta-bearing season's own dates) — same tenue
   background, font, padding and border treatment as zone A, mirrored.
   Only the delta-bearing season (deltaTargetSeasons' `current`, always
   the most-recent selected) feeds zone C; computed once per render as
   `deltaSeasonResult` rather than re-attaching Δ to that season's own
   7-column block. */
var ZONE_A_BG = "#f5f7fa";
var STICKY_TH_Z = 15;
var MONTH_COL_WIDTH = 60;
var HIST_CASES_WIDTH = 68;
var HIST_PROFIT_WIDTH = 92;
var HIST_ACUM_WIDTH = 92;
var HIST_CASES_LEFT = MONTH_COL_WIDTH;
var HIST_PROFIT_LEFT = HIST_CASES_LEFT + HIST_CASES_WIDTH;
var HIST_ACUM_LEFT = HIST_PROFIT_LEFT + HIST_PROFIT_WIDTH;
var ZONE_A_EDGE = { borderRight: "2px solid var(--color-border)", boxShadow: "6px 0 8px -4px rgba(0,0,0,0.2)" };
function stickyLeftMonthTh(extra) { return Object.assign({}, thGroup, { position: "sticky", left: 0, width: MONTH_COL_WIDTH, minWidth: MONTH_COL_WIDTH, zIndex: STICKY_TH_Z }, extra || {}); }
function stickyLeftMonthTd(extra) { return Object.assign({}, tdMonth, { position: "sticky", left: 0, width: MONTH_COL_WIDTH, minWidth: MONTH_COL_WIDTH, background: ZONE_A_BG, zIndex: 1 }, extra || {}); }
function stickyLeftGroupTh(left, width, extra) { return Object.assign({}, thGroup, { position: "sticky", left: left, width: width, minWidth: width, zIndex: STICKY_TH_Z }, extra || {}); }
function stickyLeftTh(left, width, extra) { return Object.assign({}, th, { position: "sticky", left: left, width: width, minWidth: width, zIndex: STICKY_TH_Z }, extra || {}); }
function stickyLeftTd(left, width, extra) { return Object.assign({}, td, { position: "sticky", left: left, width: width, minWidth: width, background: ZONE_A_BG, zIndex: 1 }, extra || {}); }

/* Δ Profit + Δ Acumulado are the zone B/C boundary now (Month2 moved
   to the far end per the reordered column layout below) — they carry
   the boundary border+shadow, mirrored to the left since zone C sits
   on the opposite side of the table from zone A. Month2 plays Month's
   old role: a lone column with just a plain divider facing the group
   next to it, no shadow, since it no longer touches zone B directly.
   th/td bodies are `th`/`tdMonth` with NO added fontSize/fontWeight —
   zone C must render at exactly zone A's type scale, not its own. */
var DELTA_COL_WIDTH = 90;
var MONTH2_COL_WIDTH = 80;
var ZONE_C_BOUNDARY = { borderLeft: "2px solid var(--color-border)", boxShadow: "-6px 0 8px -4px rgba(0,0,0,0.2)" };
var ZONE_C_DIVIDER = { borderLeft: "2px solid var(--color-border)" };
function stickyMonth2Th(extra) { return Object.assign({}, thGroup, { position: "sticky", right: 0, width: MONTH2_COL_WIDTH, minWidth: MONTH2_COL_WIDTH, zIndex: STICKY_TH_Z }, ZONE_C_DIVIDER, extra || {}); }
function stickyMonth2Td(extra) { return Object.assign({}, tdMonth, { position: "sticky", right: 0, width: MONTH2_COL_WIDTH, minWidth: MONTH2_COL_WIDTH, background: ZONE_A_BG, zIndex: 1 }, ZONE_C_DIVIDER, extra || {}); }
var stickyDeltaAcumuladoTh = Object.assign({}, th, { position: "sticky", right: MONTH2_COL_WIDTH, width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH, zIndex: STICKY_TH_Z, whiteSpace: "pre-line", lineHeight: 1.3 });
var stickyDeltaProfitTh = Object.assign({}, th, { position: "sticky", right: MONTH2_COL_WIDTH + DELTA_COL_WIDTH, width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH, zIndex: STICKY_TH_Z, whiteSpace: "pre-line", lineHeight: 1.3 }, ZONE_C_BOUNDARY);
function stickyDeltaAcumuladoTd(extra) { return Object.assign({}, td, { position: "sticky", right: MONTH2_COL_WIDTH, width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH, background: ZONE_A_BG, zIndex: 1 }, extra || {}); }
function stickyDeltaProfitTd(extra) { return Object.assign({}, td, { position: "sticky", right: MONTH2_COL_WIDTH + DELTA_COL_WIDTH, width: DELTA_COL_WIDTH, minWidth: DELTA_COL_WIDTH, background: ZONE_A_BG, zIndex: 1 }, ZONE_C_BOUNDARY, extra || {}); }

export default function SeasonComparison({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang];

  var [seasons, setSeasons] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filterSeasons, setFilterSeasons] = useState([]);
  var [defaultsApplied, setDefaultsApplied] = useState(false);

  useEffect(function () {
    var rows = loadLS("gfi-seasons", []);
    setSeasons(rows);
    setLoading(false);
  }, []);

  /* Not persisted across sessions — same non-persistence rule as the
     rest of the app's season filters (CLAUDE.md REGLA DE FILTRO DE
     SEASON), just with a different default selection rule (3 most
     recent Closed + the active Open, instead of "all Open"). */
  useEffect(function () {
    if (!defaultsApplied && seasons.length > 0) {
      setFilterSeasons(defaultComparisonSeasonIds(seasons));
      setDefaultsApplied(true);
    }
  }, [seasons, defaultsApplied]);

  var selectedSeasons = useMemo(function () {
    return seasons
      .filter(function (s) { return filterSeasons.indexOf(String(s.id)) >= 0; })
      .sort(function (a, b) { return (a.startDate || "").localeCompare(b.startDate || ""); }); // oldest -> newest, matches the reference sheet (active season rightmost)
  }, [seasons, filterSeasons]);

  var openSeason = useMemo(function () { return selectedSeasons.find(function (s) { return isSeasonOpen(s.status); }); }, [selectedSeasons]);

  var historicalAccumulated = useMemo(function () { return computeHistoricalAccumulated(seasons); }, [seasons]);

  var mostRecentClosedSeason = useMemo(function () { return findMostRecentClosedSeason(seasons); }, [seasons]);

  var deltaTarget = useMemo(function () { return deltaTargetSeasons(selectedSeasons); }, [selectedSeasons]);

  var seasonResults = useMemo(function () {
    return selectedSeasons.map(function (s) {
      var data = computeSeasonMonthlyComparison(s);
      var isOpen = isSeasonOpen(s.status);
      var hasDelta = !!(deltaTarget.current && String(s.id) === String(deltaTarget.current.id));
      var deltas = hasDelta ? computeDeltaColumns(data.rows, deltaTarget.previous) : null;
      return { season: s, data: data, isOpen: isOpen, hasDelta: hasDelta, deltas: deltas };
    });
  }, [selectedSeasons, deltaTarget]);

  /* Fase 5 metrics cards. Reuses seasonResults/computeSeasonMonthlyComparison
     entirely — no aggregation logic is re-derived here, only comparisons
     across what those already computed. Gate: needs an Open season AND
     at least one Closed season with real data, per spec. "Same point in
     time" = same position in the Aug->Jul cycle (see ytdThroughPosition's
     precision note in seasonComparisonCalc.js — the data is monthly, not
     daily, so this is the finest granularity honestly available). */
  var cardsData = useMemo(function () {
    if (!openSeason) return null;
    var closedSeasons = seasons.filter(function (s) { return s.status === "closed"; });
    var closedRowsBySeasons = closedSeasons.map(function (s) { return { season: s, rows: computeSeasonMonthlyComparison(s).rows }; });
    var hasClosedWithData = closedRowsBySeasons.some(function (x) { return x.rows.some(function (r) { return r.profit != null; }); });
    if (!hasClosedWithData) return null;

    var openResult = seasonResults.find(function (sr) { return sr.season.id === openSeason.id; });
    if (!openResult) return null;

    var today = new Date();
    var pos = cyclePositionForDate(today);
    var todayMonth = today.getMonth() + 1;

    var activeYtd = ytdThroughPosition(openResult.data.rows, pos);
    var closedYtds = closedRowsBySeasons.map(function (x) { return ytdThroughPosition(x.rows, pos); });
    var casesVals = closedYtds.map(function (y) { return y.cases; }).filter(function (v) { return v != null; });
    var profitVals = closedYtds.map(function (y) { return y.profit; }).filter(function (v) { return v != null; });
    var avgCases = casesVals.length > 0 ? casesVals.reduce(function (a, b) { return a + b; }, 0) / casesVals.length : null;
    var avgProfit = profitVals.length > 0 ? profitVals.reduce(function (a, b) { return a + b; }, 0) / profitVals.length : null;

    var casesPct = (activeYtd.cases != null && avgCases) ? ((activeYtd.cases / avgCases) - 1) * 100 : null;
    var profitPct = (activeYtd.profit != null && avgProfit) ? ((activeYtd.profit / avgProfit) - 1) * 100 : null;

    var bestMonth = null;
    closedRowsBySeasons.forEach(function (x) {
      x.rows.forEach(function (r) {
        if (r.profit != null && (!bestMonth || r.profit > bestMonth.profit)) {
          bestMonth = { profit: r.profit, cases: r.cases, month: r.month, year: r.year, seasonLbl: seasonLabel(x.season) };
        }
      });
    });

    /* Deliberately independent of sr.hasDelta/sr.deltas (the table's Δ
       columns are filter-dependent now) — this card is always "real
       active season vs its real predecessor", so it computes its own
       delta directly against mostRecentClosedSeason. */
    var todayDelta = computeDeltaColumns(openResult.data.rows, mostRecentClosedSeason)[todayMonth];
    var todayRow = openResult.data.rows.find(function (r) { return r.month === todayMonth; });
    var priorRow = mostRecentClosedSeason ? computeSeasonMonthlyComparison(mostRecentClosedSeason).rows.find(function (r) { return r.month === todayMonth; }) : null;
    var priorPct = (todayDelta.deltaProfit != null && priorRow && priorRow.profit) ? (todayDelta.deltaProfit / Math.abs(priorRow.profit)) * 100 : null;

    return {
      casesYtd: activeYtd.cases, casesPct: casesPct,
      profitYtd: activeYtd.profit, profitPct: profitPct,
      bestMonth: bestMonth,
      todayMonth: todayMonth, todayDelta: todayDelta, priorPct: priorPct,
      todayCases: todayRow ? todayRow.cases : null,
    };
  }, [openSeason, seasons, seasonResults, mostRecentClosedSeason]);

  if (loading) return null;

  var histFooter = (function () {
    var totalCases = 0, totalProfit = 0;
    historicalAccumulated.forEach(function (r) { if (r.cases != null) totalCases += r.cases; if (r.profit != null) totalProfit += r.profit; });
    return { cases: totalCases, profit: totalProfit };
  })();

  /* The one season (if any) carrying Δ data — rendered in zone C,
     detached from its own 7-column zone B block. seasonResults'
     underlying hasDelta/deltas fields are untouched (handleExport
     below still reads them the same way) — only the on-screen JSX
     layout moves where Δ is drawn. */
  var deltaSeasonResult = seasonResults.find(function (sr) { return sr.hasDelta; }) || null;

  var handleExport = function () {
    var exportSeasons = seasonResults.map(function (sr) {
      return { season: sr.season, data: sr.data, hasDelta: sr.hasDelta, deltas: sr.deltas, label: seasonLabel(sr.season), footer: computeSeasonFooter(sr.data.rows) };
    });
    exportSeasonComparisonXlsx({ t: t, historicalAccumulated: historicalAccumulated, histFooter: histFooter, seasonResults: exportSeasons });
  };

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: 0 }}>{t.subtitle}</p>
      </div>

      {seasons.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.noSeasons}</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)" }}>{t.seasonsFilter}</span>
            <div style={{ minWidth: 260 }}>
              <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} />
            </div>
            {/* External Select All / Clear All pair, kept together next to
                the filter — added here rather than inside SearchSelect.js
                (which already has its own internal "Clear all" row) since
                that component is shared across every module in the app;
                a scoped external pair avoids touching shared behavior. */}
            <button onClick={function () { setFilterSeasons(seasons.map(function (s) { return String(s.id); })); }} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 500 }}>{t.selectAll}</button>
            <button onClick={function () { setFilterSeasons([]); }} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 500 }}>{t.clearAllBtn}</button>
            {selectedSeasons.length > 0 && (
              <button onClick={handleExport} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.exportExcel}</button>
            )}
          </div>

          {cardsData && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>{t.cardSeasonProgress}</div>
                <div style={metricValueStyle}>{cardsData.casesYtd == null ? "—" : fmtInt(cardsData.casesYtd)}</div>
                <div style={metricSubStyle}>{t.casesYtd} · <ArrowPct pct={cardsData.casesPct} /> {t.vsHistAvg}</div>
              </div>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>{t.cardProfitYtd}</div>
                <div style={metricValueStyle}>{cardsData.profitYtd == null ? "—" : fmtMoney(cardsData.profitYtd)}</div>
                <div style={metricSubStyle}><ArrowPct pct={cardsData.profitPct} /> {t.vsHistAvg}</div>
              </div>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>{t.cardBestMonth}</div>
                {cardsData.bestMonth ? (
                  <>
                    <div style={metricValueStyle}>{t.months[cardsData.bestMonth.month - 1]} {cardsData.bestMonth.year}</div>
                    <div style={metricSubStyle}>{cardsData.bestMonth.seasonLbl} · {fmtMoney(cardsData.bestMonth.profit)} · {fmtInt(cardsData.bestMonth.cases)} {t.colCases.toLowerCase()}</div>
                  </>
                ) : <div style={metricSubStyle}>{t.noData}</div>}
              </div>
              <div style={metricCardStyle}>
                <div style={metricLabelStyle}>{t.cardVsPriorYear}</div>
                <div style={metricValueStyle}>{t.months[cardsData.todayMonth - 1]}: <span style={{ color: deltaColor(cardsData.todayDelta.deltaProfit) }}>{fmtDelta(cardsData.todayDelta.deltaProfit)}</span></div>
                <div style={metricSubStyle}><ArrowPct pct={cardsData.priorPct} /> {t.vsHistAvg}</div>
              </div>
            </div>
          )}

          {openSeason && (function () {
            var today = new Date();
            var todayISO = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");
            var start = openSeason.startDate ? new Date(openSeason.startDate + "T00:00:00") : null;
            var end = openSeason.endEstimate ? new Date(openSeason.endEstimate + "T00:00:00") : null;
            var elapsed = start ? Math.max(0, Math.round((today - start) / 86400000)) : 0;
            var total = start && end ? Math.max(1, Math.round((end - start) / 86400000)) : 0;
            var cardStyle = { flex: "1 1 140px", textAlign: "center", background: "var(--color-bg)", borderRadius: 10, padding: "10px 8px" };
            var valStyle = { fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums", fontWeight: 700, fontSize: 16, color: "var(--color-accent)" };
            var lblStyle = { fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 };
            return (
              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={cardStyle}><div style={valStyle}>{fmtDate(openSeason.startDate)}</div><div style={lblStyle}>{t.seasonStart}</div></div>
                <div style={cardStyle}><div style={valStyle}>{fmtDate(openSeason.endEstimate)}</div><div style={lblStyle}>{t.endOfSeason}</div></div>
                <div style={cardStyle}><div style={valStyle}>{fmtDate(todayISO)}</div><div style={lblStyle}>{t.today}</div></div>
                <div style={cardStyle}><div style={valStyle}>{elapsed}{total > 0 ? " / " + total : ""}</div><div style={lblStyle}>{t.daysElapsed}</div></div>
              </div>
            );
          })()}

          {selectedSeasons.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.selectSeasons}</div>
          ) : (
            <div style={{ overflowX: "auto", border: "1px solid var(--color-border)", borderRadius: 8 }}>
              <table style={{ borderCollapse: "separate", borderSpacing: 0, fontVariantNumeric: "tabular-nums" }}>
                <thead>
                  <tr>
                    <th style={stickyLeftMonthTh({ borderRight: "2px solid var(--color-border)" })} rowSpan={2}>{t.colMonth}</th>
                    <th style={stickyLeftGroupTh(HIST_CASES_LEFT, HIST_CASES_WIDTH + HIST_PROFIT_WIDTH + HIST_ACUM_WIDTH, ZONE_A_EDGE)} colSpan={3}>{t.historicalAccumulated}</th>
                    {seasonResults.map(function (sr) {
                      return <th key={sr.season.id} style={{ ...thGroup, borderRight: "2px solid var(--color-border)" }} colSpan={7}>{seasonLabel(sr.season)}</th>;
                    })}
                    {deltaSeasonResult && <th style={Object.assign({}, thGroup, { position: "sticky", right: MONTH2_COL_WIDTH, width: DELTA_COL_WIDTH * 2, minWidth: DELTA_COL_WIDTH * 2, zIndex: STICKY_TH_Z }, ZONE_C_BOUNDARY)} colSpan={2}>{t.yoyChange}</th>}
                    {deltaSeasonResult && <th style={stickyMonth2Th()} rowSpan={2}>{t.colMonth}</th>}
                  </tr>
                  <tr>
                    <th style={stickyLeftTh(HIST_CASES_LEFT, HIST_CASES_WIDTH)}>{t.colCases}</th>
                    <th style={stickyLeftTh(HIST_PROFIT_LEFT, HIST_PROFIT_WIDTH)}>{t.colProfit}</th>
                    <th style={stickyLeftTh(HIST_ACUM_LEFT, HIST_ACUM_WIDTH, ZONE_A_EDGE)}>{t.colAcumulado}</th>
                    {seasonResults.map(function (sr) {
                      return (
                        <Fragment key={sr.season.id}>
                          <th style={th}>{t.colMonth}</th>
                          <th style={th}>{t.colCases}</th>
                          <th style={th}>{t.colProfit}</th>
                          <th style={th}>{t.colAcumulado}</th>
                          <th style={th}>{t.colProfitBox}</th>
                          <th style={th}>{t.colProfitDay}</th>
                          <th style={{ ...th, borderRight: "2px solid var(--color-border)" }}>{t.colProfitHour}</th>
                        </Fragment>
                      );
                    })}
                    {deltaSeasonResult && <th style={stickyDeltaProfitTh}>{t.colDeltaProfit}{deltaTarget.previous ? <><br />{"(vs " + seasonLabel(deltaTarget.previous) + ")"}</> : null}</th>}
                    {deltaSeasonResult && <th style={stickyDeltaAcumuladoTh}>{t.colDeltaAcumulado}{deltaTarget.previous ? <><br />{"(vs " + seasonLabel(deltaTarget.previous) + ")"}</> : null}</th>}
                  </tr>
                </thead>
                <tbody>
                  {SEASON_MONTH_ORDER.map(function (m) {
                    var hist = historicalAccumulated.find(function (r) { return r.month === m; });
                    var delta = deltaSeasonResult ? deltaSeasonResult.deltas[m] : null;
                    return (
                      <tr key={m} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td style={stickyLeftMonthTd({ borderRight: "2px solid var(--color-border)" })}>{t.months[m - 1]}</td>
                        <td style={stickyLeftTd(HIST_CASES_LEFT, HIST_CASES_WIDTH)}>{hist.cases == null ? "—" : fmtInt(hist.cases)}</td>
                        <td style={stickyLeftTd(HIST_PROFIT_LEFT, HIST_PROFIT_WIDTH, { color: hist.profit != null && hist.profit < 0 ? "#DC2626" : undefined })}>{hist.profit == null ? "—" : fmtMoney(hist.profit)}</td>
                        <td style={stickyLeftTd(HIST_ACUM_LEFT, HIST_ACUM_WIDTH, ZONE_A_EDGE)}>{hist.acumulado == null ? "—" : fmtMoney(hist.acumulado)}</td>
                        {seasonResults.map(function (sr) {
                          var row = sr.data.rows.find(function (r) { return r.month === m; });
                          return (
                            <Fragment key={sr.season.id}>
                              <td style={{ ...tdMonth, background: "var(--color-bg-card-solid)" }}>{t.months[m - 1]} {row.year}</td>
                              <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>{row.cases == null ? "—" : fmtInt(row.cases)}</td>
                              <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>{row.profit == null ? "—" : fmtMoney(row.profit)}</td>
                              <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>{row.acumulado == null ? "—" : fmtMoney(row.acumulado)}</td>
                              <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>{row.profitBox == null ? "—" : fmtMoney(row.profitBox)}</td>
                              <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>{row.profitDay == null ? "—" : fmtMoney(row.profitDay)}</td>
                              <td style={{ ...td, background: "var(--color-bg-card-solid)", borderRight: "2px solid var(--color-border)" }}>{row.profitHour == null ? "—" : fmtMoney(row.profitHour)}</td>
                            </Fragment>
                          );
                        })}
                        {deltaSeasonResult && (function () { var txt = fmtDelta(delta.deltaProfit); return <td style={stickyDeltaProfitTd(deltaStyleFromText(txt))}>{txt}</td>; })()}
                        {deltaSeasonResult && (function () { var txt = fmtDelta(delta.deltaAcumulado); return <td style={stickyDeltaAcumuladoTd(deltaStyleFromText(txt))}>{txt}</td>; })()}
                        {deltaSeasonResult && <td style={stickyMonth2Td()}>{t.months[m - 1]}</td>}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid var(--color-border)", background: "var(--color-bg)" }}>
                    <td style={stickyLeftMonthTd({ borderRight: "2px solid var(--color-border)", fontWeight: 800, background: ZONE_A_BG })}>{t.totalRow}</td>
                    <td style={stickyLeftTd(HIST_CASES_LEFT, HIST_CASES_WIDTH, { fontWeight: 800 })}>{fmtInt(histFooter.cases)}</td>
                    <td style={stickyLeftTd(HIST_PROFIT_LEFT, HIST_PROFIT_WIDTH, { fontWeight: 800 })}>{fmtMoney(histFooter.profit)}</td>
                    <td style={stickyLeftTd(HIST_ACUM_LEFT, HIST_ACUM_WIDTH, ZONE_A_EDGE)}>—</td>
                    {seasonResults.map(function (sr) {
                      var footer = computeSeasonFooter(sr.data.rows);
                      return (
                        <Fragment key={sr.season.id}>
                          <td style={{ ...tdMonth, background: "var(--color-bg-card-solid)" }}>—</td>
                          <td style={{ ...td, background: "var(--color-bg-card-solid)", fontWeight: 800 }}>{fmtInt(footer.cases)}</td>
                          <td style={{ ...td, background: "var(--color-bg-card-solid)", fontWeight: 800 }}>{fmtMoney(footer.profit)}</td>
                          <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>—</td>
                          <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>{fmtMoney(footer.profitBox)}</td>
                          <td style={{ ...td, background: "var(--color-bg-card-solid)" }}>{fmtMoney(footer.profitDay)}</td>
                          <td style={{ ...td, background: "var(--color-bg-card-solid)", borderRight: "2px solid var(--color-border)" }}>{fmtMoney(footer.profitHour)}</td>
                        </Fragment>
                      );
                    })}
                    {/* Dash cells, same as Historical's own Acumulado "—" above — not bold, matching zone A's number-vs-dash weight convention exactly. */}
                    {deltaSeasonResult && <td style={stickyDeltaProfitTd()}>—</td>}
                    {deltaSeasonResult && <td style={stickyDeltaAcumuladoTd()}>—</td>}
                    {deltaSeasonResult && <td style={stickyMonth2Td()}>—</td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
